import type { User } from '@/types'

import type {
    Customer,
    DispatcherAssignment,
    DispatcherDashboardData,
    DispatcherTechniciansShedule,
    DispatcherTechniciansStatistics,
    DispatcherTechniciansTasks,
    DispatcherUnassignment,
    DispatherCustomerFeedback,
    DispatherCustomerHistory,
    DispatherMapData,
} from '../../types/dispatcher/dashboard.types'
import type {
    DispacherCustomerQueryParams,
    DispatherDashboardQueryParams,
} from '../../types/dispatcher/query.params.types'
import type { TechnicianPerfomance } from '../../types/executives'
import { objectToQueryString } from '../../utils/helpers'
import { baseApi } from '../baseApi'
import type { TechnicianPerformanceQueryParams } from '../executives/response.interface'
import type {
    DateRangeQueryParams,
    MapQueryParams,
    ResponseType,
} from '../types'

const authBase = 'api/dispatchers'

export const executivesApi = baseApi.injectEndpoints({
    endpoints: builder => ({
        getDispatcherUsers: builder.query<
            ResponseType<User[]>,
            DispatherDashboardQueryParams
        >({
            query: query => `${authBase}/users`,
        }),
        getDispatcherDashboard: builder.query<
            ResponseType<DispatcherDashboardData>,
            DispatherDashboardQueryParams
        >({
            query: query =>
                `${authBase}/dashboard?${objectToQueryString(query)}`,
        }),
        getDispatcherTechnicians: builder.query<
            ResponseType<TechnicianPerfomance[]>,
            string
        >({
            query: query => `${authBase}/technicians?${query}`,
        }),
        getDispatcherTechnicianSchedule: builder.query<
            ResponseType<DispatcherTechniciansShedule>,
            { id: number | string; query: string }
        >({
            query: ({ id, query }) =>
                `${authBase}/technicians/${id}/schedule?${query}`,
        }),
        getDispatcherTechnicianTasks: builder.query<
            ResponseType<DispatcherTechniciansTasks>,
            { id: number | string; query: string }
        >({
            query: ({ id, query }) =>
                `${authBase}/technicians/${id}/tasks?${query}`,
        }),
        getDispatcherTechnicianStatistics: builder.query<
            ResponseType<DispatcherTechniciansStatistics>,
            { id: number | string; query: string }
        >({
            query: ({ id, query }) =>
                `${authBase}/technicians/${id}/statistics?${query}`,
        }),
        getDispatcherAllCustomers: builder.query<
            ResponseType<Customer[]>,
            string
        >({
            query: query => ({
                url: `${authBase}/customers?skipOrders=true&${query}`,
                method: 'GET',
            }),
            providesTags: ['customers'],
        }),
        getDispatcherCustomerHistory: builder.query<
            ResponseType<DispatherCustomerHistory>,
            { id: number | string; query: string }
        >({
            query: ({ id, query }) =>
                `${authBase}/customers/${id}/history?${query}`,
            providesTags: ['customers'],
        }),
        getDispatcherCustomerUpcoming: builder.query<
            ResponseType<DispatherCustomerHistory>,
            { id: number | string; query: string }
        >({
            query: ({ id, query }) =>
                `${authBase}/customers/${id}/upcoming?${query}`,
        }),
        getDispatcherCustomerFeedbacks: builder.query<
            ResponseType<DispatherCustomerFeedback>,
            { id: number | string; query: string }
        >({
            query: ({ id, query }) =>
                `${authBase}/customers/${id}/feedbacks?${query}`,
        }),
        getDispatcherMap: builder.query<
            ResponseType<DispatherMapData>,
            { query: MapQueryParams }
        >({
            query: ({ query }) =>
                `${authBase}/map/?${objectToQueryString(query)}`,
        }),
        getDispatcherCalendar: builder.query<
            ResponseType<DispatcherAssignment[]>,
            { query: DateRangeQueryParams }
        >({
            query: ({ query }) =>
                `${authBase}/calendar/?${objectToQueryString(query)}`,
        }),
        getDispatcherUnassignedOrders: builder.query<
            ResponseType<DispatcherUnassignment[]>,
            { query: DateRangeQueryParams }
        >({
            query: ({ query }) =>
                `${authBase}/orders/?${objectToQueryString(query)}`,
        }),
        assignTechnicianToOrder: builder.mutation<
            ResponseType<DispatcherAssignment>,
            {
                orderId: number
                body: { id: number } // user id
            }
        >({
            query: ({ orderId, body }) => ({
                url: `${authBase}/orders/${orderId}/assign`,
                method: 'POST',
                body,
            }),
        }),
        reassignTechnicianToOrder: builder.mutation<
            ResponseType<DispatcherAssignment>,
            {
                orderId: number
                body: { id: number } // user id
            }
        >({
            query: ({ orderId, body }) => ({
                url: `${authBase}/orders/${orderId}/assign`,
                method: 'PUT',
                body,
            }),
        }),
    }),
})
export const {
    useGetDispatcherMapQuery,
    useGetDispatcherCalendarQuery,
    useGetDispatcherUnassignedOrdersQuery,
    useGetDispatcherDashboardQuery,
    useGetDispatcherTechniciansQuery,
    useGetDispatcherTechnicianScheduleQuery,
    useGetDispatcherTechnicianTasksQuery,
    useGetDispatcherTechnicianStatisticsQuery,
    useGetDispatcherAllCustomersQuery,
    useGetDispatcherCustomerHistoryQuery,
    useGetDispatcherCustomerUpcomingQuery,
    useGetDispatcherCustomerFeedbacksQuery,
    useLazyGetDispatcherUsersQuery,
    useAssignTechnicianToOrderMutation,
    useReassignTechnicianToOrderMutation,
    useLazyGetDispatcherTechniciansQuery,
} = executivesApi
